import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { IStoreState } from 'store/modules';
import { utilService } from '../../../services/util-service';

const COLOR: Record<number, ReturnType<typeof css>> = {
  1: css`
    background: linear-gradient(180deg, #225fba 0%, #121b4a 100%);
  `,
  2: css`
    background: linear-gradient(180deg, #2a86c8 0%, #143357 100%);
  `,
  3: css`
    background: linear-gradient(180deg, #2db5d3 0%, #0f414c 100%);
  `,
  4: css`
    background: linear-gradient(180deg, #36c2b1 0%, #134942 100%);
  `,
  5: css`
    background: linear-gradient(180deg, #00315c 0%, #000000 100%);
  `,
};

interface AccordionProps {
  lv?: number;
}

interface NodeProps {
  node: any;
  expanded?: boolean;
  onCheckboxChange: (value: string, checked: boolean) => void;
}

const Accordion = ({ node, expanded = false, onCheckboxChange }: NodeProps) => {
  const users = useSelector((store: IStoreState) => store.MainReducer.users);
  const [flag, setFlag] = useState(false);
  const [user, setUser] = useState<any>(undefined);
  const [checked, setChecked] = useState(!!node.checked);

  const checkboxId = `checkbox-${node.value}`;

  useEffect(() => {
    setFlag(expanded);
  }, [expanded, node.value]);

  useEffect(() => {
    const targetUser = users.rDatas.find((data) => data.phone === node.value);
    setUser(targetUser);
  }, [users, node.value]);

  useEffect(() => {
    setChecked(!!node.checked);
    if (node.checked) {
      setFlag(true);
    }
  }, [node.checked]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked: isChecked } = event.target;
    setChecked(isChecked);
    onCheckboxChange(value, isChecked);

    const updateTreeData = (currentNode: any) => {
      if (currentNode.children) {
        currentNode.children.forEach((child: any) => onCheckboxChange(child.value, isChecked));
        currentNode.children.forEach((child: any) => updateTreeData(child));
      }
    };
    updateTreeData(node);
  };

  return (
    <AccordionTemplate>
      <AccordionWrap lv={node.lv}>
        <ChckedWrap>
          <label htmlFor={checkboxId} className="visually-hidden">
            {node.label || '선택'}
          </label>
          <input
            id={checkboxId}
            type="checkbox"
            value={node.value}
            checked={!!checked}
            onChange={handleCheckboxChange}
          />
        </ChckedWrap>
        {node.lv === undefined ? (
          <UserWrap>
            <DescWrap>
              {node.label}({utilService.isMobileFormat(node.value)})
              <PhoneWrap>
                {node.pCnt}회{node.vesselinfo && <>&ensp;|&ensp;{node.vesselinfo}</>}
              </PhoneWrap>
            </DescWrap>
            <StatusWrap>
              {user !== undefined &&
                (user.isConnect ? (
                  <img src={process.env.PUBLIC_URL + '/images/icon_send.svg'} alt="Send" />
                ) : (
                  <img src={process.env.PUBLIC_URL + '/images/icon_connect.svg'} alt="Connect" />
                ))}
            </StatusWrap>
          </UserWrap>
        ) : (
          <div>{node.label}</div>
        )}

        {node.children && node.children.length >= 1 && (
          <button type="button" onClick={() => setFlag(!flag)}>
            {!flag ? (
              <img src={process.env.PUBLIC_URL + '/images/icon_expand.svg'} alt="Expand" />
            ) : (
              <img src={process.env.PUBLIC_URL + '/images/icon_fold.svg'} alt="Fold" />
            )}
          </button>
        )}
      </AccordionWrap>

      <div>
        {node.children &&
          flag &&
          node.children.map((child: any) => (
            <Accordion key={child.value} node={child} onCheckboxChange={onCheckboxChange} expanded={true} />
          ))}
      </div>
    </AccordionTemplate>
  );
};

const AccordionTemplate = styled.div`
  width: 18.75rem;
  border-bottom: 1px solid #7d7d7d;
`;
const ChckedWrap = styled.div`
  height: 18px;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;
const AccordionWrap = styled.div<AccordionProps>`
  display: flex;
  height: ${(props) => `${props.lv ? 2.5 : 3.5}rem`};
  margin-bottom: 0.5rem;
  align-items: center;
  color: #fff;
  text-align: center;
  font-family: 'Noto Sans CJK KR';
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin-right: auto;
  padding-right: 0.625rem;
  padding-left: ${(props) => (props.lv ? props.lv * 0.5 + 'rem' : '2rem')};
  ${(props) => props.lv && COLOR[props.lv]}
  button {
    background: transparent;
    border: none;
    margin-left: auto;
    color: #fff;
    cursor: pointer;
  }
`;
const UserWrap = styled.div`
  display: flex;
  width: 100%;
`;
const DescWrap = styled.div`
  text-align: left;
  padding: 5px;
  white-space: nowrap;
  overflow: hidden;
  width: calc(100% - 2rem);
`;
const PhoneWrap = styled.div`
  text-align: left;
  font-family: 'Noto Sans CJK KR';
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  color: #f2ffa1;
`;
const StatusWrap = styled.div`
  position: static;
  margin-left: auto;
  right: 1rem;
  align-self: center;
`;

export default Accordion;
